import { FC, useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useSnackbar } from 'notistack'
import { privacidadeService } from '@/services/privacidadeService'
import {
  ConfiguracaoPrivacidade,
  Expurgo,
  FUNDAMENTO_EXPURGO_LABEL,
  ItemExpurgo,
  ORIGEM_EXPURGO_LABEL,
} from '@/types/privacidade'
import { formatDateTime } from '@/utils/formatters'
import { ConfirmacaoPorCodigoDialog } from './ConfirmacaoPorCodigoDialog'
import { ItensExpurgoTabela } from './ItensExpurgoTabela'
import { podeCancelar, podeRejeitar, podeRetirarItem, situacaoAprovacao, temAlertaReconciliacao } from './aprovacao'
import {
  ChipStatusExpurgo,
  codigoErro,
  mensagemErroPrivacidade,
  rotuloAtor,
  useAcessoPrivacidade,
} from './comum'

interface Props {
  /** Id do expurgo aberto; `null` fecha. */
  expurgoId: string | null
  configuracao: ConfiguracaoPrivacidade | null
  onFechar: () => void
  /** Algo mudou (aprovação, rejeição, cancelamento) — a lista recarrega. */
  onAlterado: () => void
}

type Acao = 'aprovar' | 'rejeitar' | 'cancelar' | 'retirar'

const Passo: FC<{ titulo: string; quem?: string | null; quando?: string | null; eu: string | null }> = ({
  titulo,
  quem,
  quando,
  eu,
}) => (
  <Box sx={{ borderLeft: 3, borderColor: quem ? 'success.main' : 'divider', pl: 1.5 }}>
    <Typography variant="body2" fontWeight={600}>
      {titulo}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {quem ? `${rotuloAtor(quem, eu)} · ${formatDateTime(quando)}` : 'pendente'}
    </Typography>
  </Box>
)

/**
 * Detalhe do expurgo (AN-12 §5.2–5.3, §12): itens com motivo de inclusão, confirmações por
 * módulo, as três pessoas (solicitante, 1ª DPO, 2ª ADMIN) e as ações de quem pode agir.
 * Aprovar pede o código digitado e manda o `previaHash` que a pessoa está vendo — se o
 * conjunto mudou, o servidor responde 409 PREVIA_DESATUALIZADA e a tela recarrega.
 */
export const ExpurgoDetalheDrawer: FC<Props> = ({ expurgoId, configuracao, onFechar, onAlterado }) => {
  const { enqueueSnackbar } = useSnackbar()
  const acesso = useAcessoPrivacidade()
  const [expurgo, setExpurgo] = useState<Expurgo | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [acao, setAcao] = useState<Acao | null>(null)
  /** Item que o DPO vai retirar (ação 'retirar'). */
  const [itemAlvo, setItemAlvo] = useState<ItemExpurgo | null>(null)
  const [processando, setProcessando] = useState(false)

  const carregar = useCallback(async (id: string) => {
    setErro(null)
    try {
      setExpurgo(await privacidadeService.expurgo(id))
    } catch (e) {
      setErro(mensagemErroPrivacidade(e, 'Falha ao carregar o expurgo'))
    }
  }, [])

  useEffect(() => {
    setExpurgo(null)
    setAcao(null)
    if (expurgoId) carregar(expurgoId)
  }, [expurgoId, carregar])

  const executar = async (motivo: string) => {
    if (!expurgo || !acao) return
    setProcessando(true)
    try {
      const atualizado =
        acao === 'aprovar'
          ? await privacidadeService.aprovar(expurgo.id, expurgo.previaHash, expurgo.versao)
          : acao === 'rejeitar'
            ? await privacidadeService.rejeitar(expurgo.id, motivo)
            : acao === 'retirar' && itemAlvo
              ? await privacidadeService.retirarItem(expurgo.id, itemAlvo.documentoId, motivo)
              : await privacidadeService.cancelar(expurgo.id)
      enqueueSnackbar(
        acao === 'aprovar'
          ? `Aprovação registrada em ${expurgo.codigo}`
          : acao === 'rejeitar'
            ? `${expurgo.codigo} rejeitado`
            : acao === 'retirar'
              ? `Item retirado de ${expurgo.codigo}`
              : `${expurgo.codigo} cancelado`,
        { variant: 'success' }
      )
      setAcao(null)
      setItemAlvo(null)
      if (atualizado && typeof atualizado === 'object' && 'id' in atualizado) setExpurgo(atualizado)
      else await carregar(expurgo.id)
      onAlterado()
    } catch (e) {
      enqueueSnackbar(mensagemErroPrivacidade(e, 'Falha ao registrar a ação'), { variant: 'error' })
      setAcao(null)
      setItemAlvo(null)
      // Prévia mudou, hold novo (o expurgo JÁ foi para BLOQUEADO_RETENCAO), outra pessoa agiu
      // ao mesmo tempo: o que está na tela não vale mais — recarrega.
      const codigo = codigoErro(e)
      if (
        codigo === 'PREVIA_DESATUALIZADA' ||
        codigo === 'DOCUMENTO_EM_RETENCAO_LEGAL' ||
        codigo === 'CONFLITO_VERSAO' ||
        codigo === 'EXPURGO_ESTADO_INVALIDO' ||
        codigo === 'APROVADOR_NAO_DISTINTO' ||
        codigo === 'ITEM_POSSIVELMENTE_EXECUTADO' ||
        codigo === 'ITEM_NAO_RETIRAVEL'
      ) {
        await carregar(expurgo.id)
        onAlterado()
      }
    } finally {
      setProcessando(false)
    }
  }

  const situacao = expurgo ? situacaoAprovacao(expurgo, acesso) : null
  const eu = acesso.usuarioId

  return (
    <Drawer anchor="right" open={!!expurgoId} onClose={onFechar}>
      <Box sx={{ width: { xs: '100vw', sm: 640 }, p: 3 }} role="dialog" aria-label={expurgo?.codigo ?? 'Expurgo'}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            {expurgo?.codigo ?? 'Expurgo'}
          </Typography>
          <IconButton onClick={onFechar} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </Stack>

        {erro && <Alert severity="error">{erro}</Alert>}
        {!expurgo && !erro && <CircularProgress size={24} aria-label="Carregando expurgo" />}

        {expurgo && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <ChipStatusExpurgo
                status={expurgo.status}
                expurgoFisicoHabilitado={configuracao?.expurgoFisicoHabilitado}
                aguardandoHabilitacao={expurgo.aguardandoHabilitacao}
              />
              <Chip size="small" variant="outlined" label={ORIGEM_EXPURGO_LABEL[expurgo.origem] ?? expurgo.origem} />
              {temAlertaReconciliacao(expurgo) && (
                <Chip size="small" color="warning" label="Cópias sem confirmação — reconciliação em curso" />
              )}
            </Stack>

            {(expurgo.aguardandoHabilitacao ||
              (expurgo.status === 'APROVADO' && configuracao?.expurgoFisicoHabilitado === false)) && (
              <Alert severity="warning">
                Aprovado — aguardando habilitação do expurgo físico. Nenhum arquivo será apagado enquanto a
                flag estiver desligada.
              </Alert>
            )}
            {expurgo.status === 'APROVADO' && expurgo.executarApos && (
              <Typography variant="body2" color="text.secondary">
                Carência técnica até {formatDateTime(expurgo.executarApos)} — ainda dá para rejeitar.
              </Typography>
            )}
            {expurgo.status === 'BLOQUEADO_RETENCAO' && (
              <Alert severity="error">
                Bloqueado por retenção legal. Fica assim até a liberação e uma nova aprovação dupla.
              </Alert>
            )}
            {expurgo.status === 'EXECUTANDO' &&
              (expurgo.itens ?? []).some((i) => i.status === 'ERRO') && (
                <Alert severity="warning" data-testid="aviso-itens-com-erro">
                  Há item com erro. Item em retenção legal volta a andar quando o hold for liberado; o DPO pode
                  retirar do expurgo um item que o storage nunca tocou para o restante concluir.
                </Alert>
              )}

            <Box>
              <Typography variant="subtitle2">Fundamento</Typography>
              <Typography variant="body2">
                {FUNDAMENTO_EXPURGO_LABEL[expurgo.fundamentoTipo] ?? expurgo.fundamentoTipo}
                {expurgo.solicitacaoProtocolo ? ` · ${expurgo.solicitacaoProtocolo}` : ''}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {expurgo.fundamentoTexto}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Aprovações (três pessoas distintas)
              </Typography>
              <Stack spacing={1} data-testid="aprovacoes">
                <Passo titulo="Solicitado" quem={expurgo.solicitadoPor} quando={expurgo.solicitadoEm} eu={eu} />
                <Passo titulo="1ª aprovação — DPO" quem={expurgo.aprovado1Por} quando={expurgo.aprovado1Em} eu={eu} />
                <Passo titulo="2ª aprovação — ADMIN" quem={expurgo.aprovado2Por} quando={expurgo.aprovado2Em} eu={eu} />
                {expurgo.canceladoPor && (
                  <Alert severity="info">
                    Cancelado por {rotuloAtor(expurgo.canceladoPor, eu)} em {formatDateTime(expurgo.canceladoEm)}
                  </Alert>
                )}
                {expurgo.rejeitadoPor && (
                  <Alert severity="info">
                    Rejeitado por {rotuloAtor(expurgo.rejeitadoPor, eu)} em {formatDateTime(expurgo.rejeitadoEm)}
                    {expurgo.motivoRejeicao ? `: ${expurgo.motivoRejeicao}` : ''}
                  </Alert>
                )}
              </Stack>
            </Box>

            {situacao && (situacao.tipo === 'jaAtuou' || situacao.tipo === 'outroPapel') && (
              <Alert severity="info" data-testid="aviso-aprovacao">
                {situacao.mensagem}
              </Alert>
            )}

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {situacao?.tipo === 'pode' && (
                <Button variant="contained" onClick={() => setAcao('aprovar')}>
                  {situacao.etapa === 1 ? 'Aprovar (1ª — DPO)' : 'Aprovar (2ª — ADMIN)'}
                </Button>
              )}
              {podeRejeitar(expurgo.status, acesso) && (
                <Button variant="outlined" color="error" onClick={() => setAcao('rejeitar')}>
                  Rejeitar
                </Button>
              )}
              {podeCancelar(expurgo, acesso) && (
                <Button variant="text" onClick={() => setAcao('cancelar')}>
                  Cancelar pedido
                </Button>
              )}
            </Stack>

            <Divider />
            <Typography variant="subtitle2">
              Arquivos ({expurgo.itens?.length ?? expurgo.totalItens ?? 0})
            </Typography>
            {expurgo.itens && expurgo.itens.length > 0 ? (
              <ItensExpurgoTabela
                itens={expurgo.itens}
                confirmacoesEsperadas={configuracao?.confirmacoesEsperadas}
                acaoItem={(item) =>
                  podeRetirarItem(expurgo, item, acesso) ? (
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      onClick={() => {
                        setItemAlvo(item)
                        setAcao('retirar')
                      }}
                    >
                      Retirar item
                    </Button>
                  ) : null
                }
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Sem itens no detalhe.
              </Typography>
            )}
            {expurgo.trilha && expurgo.trilha.length > 0 && (
              <>
                <Typography variant="subtitle2">Trilha deste expurgo</Typography>
                <Stack spacing={0.5} data-testid="trilha-expurgo">
                  {expurgo.trilha.map((r) => (
                    <Typography key={r.id} variant="caption" color="text.secondary">
                      {formatDateTime(r.ocorridoEm)} · {r.evento}
                      {r.documentoId ? ` · ${r.documentoId.slice(0, 8)}` : ''} · {rotuloAtor(r.ator, eu)}
                    </Typography>
                  ))}
                </Stack>
              </>
            )}
          </Stack>
        )}

        {expurgo && (
          <ConfirmacaoPorCodigoDialog
            aberto={!!acao}
            codigo={expurgo.codigo}
            titulo={
              acao === 'aprovar'
                ? `Aprovar ${expurgo.codigo}`
                : acao === 'rejeitar'
                  ? `Rejeitar ${expurgo.codigo}`
                  : acao === 'retirar'
                    ? `Retirar item de ${expurgo.codigo}`
                    : `Cancelar ${expurgo.codigo}`
            }
            descricao={
              acao === 'aprovar'
                ? 'A aprovação fica presa a esta prévia: se aparecer versão ou cópia nova, ela é recusada. O servidor confere retenção legal de novo.'
                : acao === 'rejeitar'
                  ? 'A rejeição encerra o expurgo; um novo pedido recomeça as aprovações.'
                  : acao === 'retirar'
                    ? `O documento ${itemAlvo?.documentoId.slice(0, 8) ?? ''} sai deste expurgo e não será apagado por ele; o restante segue. Só vale para item que o storage nunca tocou.`
                    : 'O pedido é cancelado antes da 1ª aprovação.'
            }
            rotuloConfirmar={
              acao === 'aprovar'
                ? 'Aprovar'
                : acao === 'rejeitar'
                  ? 'Rejeitar'
                  : acao === 'retirar'
                    ? 'Retirar item'
                    : 'Cancelar pedido'
            }
            corConfirmar={acao === 'aprovar' ? 'primary' : 'error'}
            pedirMotivo={
              acao === 'rejeitar'
                ? { rotulo: 'Motivo da rejeição', maximo: 500 }
                : acao === 'retirar'
                  ? { rotulo: 'Motivo da retirada', maximo: 500 }
                  : undefined
            }
            processando={processando}
            onCancelar={() => {
              setAcao(null)
              setItemAlvo(null)
            }}
            onConfirmar={executar}
          />
        )}
      </Box>
    </Drawer>
  )
}

export default ExpurgoDetalheDrawer
